import React, { useEffect, useRef, useState } from "react";
import TrustBadgeGray from "assests/images/trust-badge-gray.svg";
import { Info } from "@phosphor-icons/react";
import PaymentCancelled from "assests/images/payment-cancelled.svg";
import InfoError from "assests/images/info-error.svg";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import GenericButton from "components/common/buttons/GenericButton";
import {
  PaymentStatusType,
  PaymentStatusDetails,
  PaymentRetryModes,
  PaymentRedirectionModes,
} from "lib/types/payments";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLocale } from "lib/hooks/useLocale";
import ExitPayment from "components/payments/dialogs/ExitPayment";
import { GenericDialog } from "components/common/dialogs/GenericDialog";
import { exitCheckout, publishPostMessage } from "lib/utils/helpers";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { analyticsEvents, eventTypes } from "lib/utils/constants";

interface PaymentFailedProps {
  paymentDetails?: PaymentStatusDetails;
  status?: PaymentStatusType;
}

// const PAYMENT_RETRY_COUNT_STORAGE_KEY_PREFIX = "flo_payment_retry_count_";

// PaymentFailed remounts fresh on every retry (retrying navigates away to the payment page,
// and a new failure lands back here as a new mount with a rotated checkoutId), so an in-memory
// ref can't track retry count across attempts — persist it against the stable checkoutToken instead.
// const getNextPaymentRetryNumber = (checkoutToken?: string): number => {
//   if (!checkoutToken) return 1;
//   const key = `${PAYMENT_RETRY_COUNT_STORAGE_KEY_PREFIX}${checkoutToken}`;
//   const nextRetryNumber = Number(sessionStorage.getItem(key) ?? "0") + 1;
//   sessionStorage.setItem(key, String(nextRetryNumber));
//   return nextRetryNumber;
// };

const PaymentFailed: React.FC<PaymentFailedProps> = ({ paymentDetails, status }) => {
  const {
    state: { merchant },
  } = useMerchantContext();
  const {
    state: { checkoutToken, redirectUrl },
  } = useCheckoutContext();
  const { t } = useLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const checkoutId = searchParams.get("checkoutId");
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();

  const [openExitForm, setOpenExitForm] = useState<boolean>(false);
  const hasSentEvent = useRef(false);

  useEffect(() => {
    if ((status === "PAYMENT_CANCELLED" || status === "CANCELLED") && !hasSentEvent.current) {
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_PAYMENT_CANCELLED_PAGE_LOADED,
        eventType: "load",
      });
      hasSentEvent.current = true;
    }
    publishPostMessage(eventTypes.FLO_PAYMENT_FAILED, {
      orderAmount: Number(paymentDetails?.amount),
    });
  }, [status, sendAnalyticsEvent]);

  const retrymodes: { [key: string]: PaymentRedirectionModes } = {
    CHANGE_PAYMENT_METHOD: "PAYMENTS",
    RETRY_PAYMENT: "PAYMENTS",
    CONVERT_TO_COD: "COD",
    PLACE_ORDER_AGAIN: "PAYMENTS",
  };

  const handleBtnClick = (mode: PaymentRetryModes) => {
    if (mode === "SEND_WHATSAPP_LINK") {
      return;
    }
    if (mode === "CLOSE_PAYMENT") {
      navigate(`/?tokenId=${checkoutToken}`);
      return;
    }
    if (mode === "CHANGE_PAYMENT_METHOD" && (status === "PAYMENT_CANCELLED" || status === "CANCELLED")) {
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_TRY_OTHER_METHOD_CLICKED,
        eventType: "click",
      });
    }

    // Will be handling this in the future
    // if (mode === "RETRY_PAYMENT" && (status === "PAYMENT_FAILED" || status === "FAILED")) {
    //   sendAnalyticsEvent({
    //     eventName: analyticsEvents.FLO_PAYMENT_RETRIED,
    //     eventType: "click",
    //     metaData: {
    //       paymentRetryData: {
    //         paymentMethod: paymentDetails?.paymentMode,
    //         previousFailureCode: paymentDetails?.failureReason,
    //         retryNumber: getNextPaymentRetryNumber(checkoutToken),
    //       },
    //     },
    //   });
    // }
    navigate(`/?tokenId=${checkoutToken}&old_checkout_id=${checkoutId}#${retrymodes[mode]}`);
  };

  const handleBackToStore = () => {
    if (!merchant) return;
    exitCheckout(redirectUrl ?? merchant?.brandUrl, false);
  };

  const getTitleText = () => {
    switch (status) {
      case "PAYMENT_CANCELLED":
        return t("payment_cancelled");
      case "PAYMENT_FAILED":
        return t("payment_x_failed");
      default:
        return t("polling_ended_message");
    }
  };

  return (
    <>
      <div className="h-full px-6 py-2">
        <div className="border-box flex h-full flex-col items-center">
          {/* Icon Container */}
          <div className="justify-self-top mb-auto pt-12">
            <img
              src={merchant?.logoUrl}
              alt="Brand Logo"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              className="h-10 max-w-[9.375rem] bg-transparent object-contain"
            />
          </div>
          {/* Icon Container Ends */}
          {/* Failure Message Container */}
          <div className="my-auto flex flex-col gap-6 justify-self-center">
            <div className="flex flex-col items-center gap-6 ">
              <img
                src={status === "PAYMENT_CANCELLED" ? PaymentCancelled : InfoError}
                alt="Info Error Icon"
                className="h-12 w-12 bg-transparent object-contain"
              />
              <h1 className="text-center text-lg font-medium ">{getTitleText()}</h1>
            </div>
            <div
              className={`flex gap-2 rounded-lg p-4 text-coal-dark ${
                status === "PAYMENT_CANCELLED" ? "border border-gray-light bg-[#FAFAFA]" : "bg-primary-light"
              }`}>
              <div className="mb-auto">
                <Info
                  className={`h-3.5 w-3.5 ${
                    status === "PAYMENT_CANCELLED" ? "text-coal-dark" : "text-primary-dark"
                  }`}
                />
              </div>

              <p className="text-sm font-normal">
                {status === "PAYMENT_CANCELLED"
                  ? t("amount_deducted_cancelled_warning")
                  : t("amount_deducted_failed_warning")}
              </p>
            </div>
          </div>
          {/* Failure Message Container Ends */}

          {/* Button Container */}
          <div className="mt-auto flex w-full flex-col items-center gap-4 justify-self-end">
            <GenericButton
              buttonText={t(`${paymentDetails?.primaryCTA?.toLowerCase()}`) ?? t("place_order_again")}
              height="h-14"
              customClass="bg-primary-dark text-btnPrimaryText rounded-2xl"
              onClick={() => handleBtnClick(paymentDetails?.primaryCTA ?? "PLACE_ORDER_AGAIN")}
            />
            {/* {Boolean(paymentDetails?.secondaryCTA) && (
              <GenericButton
                buttonText={
                  t(`${paymentDetails?.secondaryCTA?.toLowerCase()}`) ?? t("wait_for_order_confirmation")
                }
                height="h-14"
                customClass="border-2 rounded-2xl text-coal-dark"
                onClick={() => handleBtnClick(paymentDetails?.secondaryCTA ?? "PLACE_ORDER_AGAIN")}
              />
            )} */}
            {status === "PAYMENT_CANCELLED" && (
              <div className="flex text-sm">
                <p className="text-coal-light">Forgot something?&nbsp;</p>
                <p
                  className="cursor-pointer text-coal-dark underline decoration-coal-dark"
                  onClick={() => setOpenExitForm(true)}>
                  Back to store
                </p>
              </div>
            )}
            <div className="flex justify-center pb-2 pt-12">
              <img src={TrustBadgeGray} className="h-4" alt={"shopflo-logo"} />
            </div>
          </div>
          {/* Button Container Ends */}
        </div>
      </div>
      <GenericDialog
        isOpen={openExitForm}
        translateAxis="y"
        modalType="NONE"
        dialogOverlay={true}
        customClass="overflow-scroll md:!top-auto md:absolute">
        <ExitPayment
          setIsOpen={(value: boolean) => setOpenExitForm(value)}
          handleExitPayment={handleBackToStore}
        />
      </GenericDialog>
    </>
  );
};

export default PaymentFailed;
